import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Loader2 } from 'lucide-react';

import { Button } from '@/components/atoms/button';
import { Progress } from '@/components/atoms/progress';
import { Form } from '@/components/molecules/form-field';

import { useCountries } from '@/lib/hooks/use-countries';
import { useCreateShippingRate } from '@/features/shipping/hooks/use-create-shipping-rate';
import {
  ShippingRateBasicInfo,
  ShippingRateTypeSelector,
  ShippingRatePricing,
  ShippingRateWeightRanges,
  ShippingRatePriceRanges,
  ShippingRateDeliverySettings,
  ShippingRateCountries,
} from '@/features/shipping/components';
import {
  shippingRateSchema,
  DEFAULT_SHIPPING_RATE_VALUES,
  type ShippingRateFormData,
} from '@/features/shipping/types/shipping-form.types';
import type { CreateShippingRateData } from '@/features/shipping/types/shipping.types';

export default function NewShippingRatePage() {
  const { t } = useTranslation();

  const { data: countries, isLoading: isLoadingCountries } = useCountries();
  const { mutate: createShippingRate, isPending: isSubmitting } = useCreateShippingRate();

  const form = useForm<ShippingRateFormData>({
    resolver: zodResolver(shippingRateSchema),
    defaultValues: DEFAULT_SHIPPING_RATE_VALUES,
  });

  const weightRangesFieldArray = useFieldArray({ control: form.control, name: 'weightRanges' });
  const priceRangesFieldArray = useFieldArray({ control: form.control, name: 'priceRanges' });

  const watchRateType = form.watch('rateType');
  const watchDeliveryType = form.watch('deliveryType');

  const isPickup = watchDeliveryType === 'pickup';
  const isFreeShipping = watchRateType === 'free_shipping';
  const showFlatPricing = watchRateType === 'flat' && !isPickup && !isFreeShipping;

  // Calculate form progress
  const formValues = form.watch();
  const { progress, completedFields, totalFields } = useMemo(() => {
    const requiredFields = ['name', 'rateType', 'deliveryType', 'currency', 'availableCountries'];

    let completed = 0;
    if (formValues.name?.length >= 2) completed++;
    if (formValues.rateType) completed++;
    if (formValues.deliveryType) completed++;
    if (formValues.currency) completed++;
    if (formValues.availableCountries?.length > 0) completed++;

    if (!isFreeShipping && !isPickup) {
      if (formValues.price > 0) completed++;
    }

    const total = isFreeShipping || isPickup ? requiredFields.length : requiredFields.length + 1;

    return {
      completedFields: completed,
      totalFields: total,
      progress: (completed / total) * 100,
    };
  }, [formValues, isFreeShipping, isPickup]);

  const onSubmit = async (data: ShippingRateFormData) => {
    let price = data.price;
    if (data.rateType === 'free_shipping' || isPickup) {
      price = 0;
    }

    let rateCalculation: CreateShippingRateData['rateCalculation'] | undefined;

    if (data.rateType === 'weight_based' && data.weightRanges) {
      const validRanges = data.weightRanges.filter(
        (r) => r.minWeight > 0 || r.maxWeight > 0 || r.rate > 0
      );
      if (validRanges.length > 0) {
        rateCalculation = {
          type: data.rateType,
          weightUnit: data.weightUnit,
          weightRanges: validRanges,
        };
      }
    } else if (data.rateType === 'price_based' && data.priceRanges) {
      const validRanges = data.priceRanges.filter(
        (r) => r.minPrice > 0 || r.maxPrice > 0 || r.rate > 0
      );
      if (validRanges.length > 0) {
        rateCalculation = {
          type: data.rateType,
          priceRanges: validRanges,
        };
      }
    } else if (data.rateType === 'flat') {
      rateCalculation = { type: 'flat', baseRate: price };
    } else {
      rateCalculation = { type: data.rateType };
    }

    const payload: CreateShippingRateData = {
      name: data.name,
      description: data.description || undefined,
      deliveryType: data.deliveryType,
      price,
      currency: data.currency,
      availableCountries: data.availableCountries,
      isCustomRate: true,
    };

    if (rateCalculation) {
      payload.rateCalculation = rateCalculation;
    }

    if (data.freeShippingEnabled && data.freeShippingThreshold) {
      payload.freeShippingThreshold = data.freeShippingThreshold;
    }

    if (data.deliverySettingsEnabled) {
      if (data.estimatedDeliveryDays) payload.estimatedDeliveryDays = data.estimatedDeliveryDays;
      if (data.maxDeliveryDays) payload.maxDeliveryDays = data.maxDeliveryDays;
    }

    createShippingRate(payload);
  };

  return (
    <div className="pb-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center mb-6">
        <Button variant="ghost" size="icon" asChild className="shrink-0 self-start sm:self-auto">
          <Link to="/shipping">
            <ArrowLeft className="size-5" />
          </Link>
        </Button>
        <div className="min-w-0">
          <h1 className="text-2xl font-bold">{t('shipping.addRate', { defaultValue: 'Add Shipping Rate' })}</h1>
          <p className="text-muted-foreground">
            {t('shipping.addRateDescription', { defaultValue: 'Create a new shipping rate for your shops' })}
          </p>
        </div>
      </div>

      {/* Progress Indicator */}
      <div className="mb-6 p-4 bg-muted/50 rounded-lg">
        <div className="flex justify-between text-sm mb-2">
          <span>
            {completedFields}/{totalFields} {t('products.fieldsCompleted', { defaultValue: 'fields completed' })}
          </span>
          <span>{Math.round(progress)}%</span>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-6 lg:grid lg:grid-cols-3">
            {/* Left Column - Main Info */}
            <div className="lg:col-span-2 space-y-6">
              <ShippingRateBasicInfo form={form} />
              <ShippingRateTypeSelector form={form} />
              <ShippingRatePricing form={form} showPricing={showFlatPricing} isPickup={isPickup} />

              {watchRateType === 'weight_based' && !isPickup && (
                <ShippingRateWeightRanges form={form} fieldArray={weightRangesFieldArray} />
              )}

              {watchRateType === 'price_based' && !isPickup && (
                <ShippingRatePriceRanges form={form} fieldArray={priceRangesFieldArray} />
              )}

              <ShippingRateDeliverySettings form={form} />
            </div>

            {/* Right Column - Countries & Submit */}
            <div className="space-y-6">
              <ShippingRateCountries
                form={form}
                countries={countries}
                isLoading={isLoadingCountries}
              />

              {/* Submit Button */}
              <div className="sticky bottom-4">
                <Button type="submit" className="w-full" size="lg" disabled={isSubmitting}>
                  {isSubmitting && <Loader2 className="size-4 mr-2 animate-spin" />}
                  {t('shipping.createRate', { defaultValue: 'Create Shipping Rate' })}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}
